import { MUI, MUIIcons, ThemeMode } from 'ci-esi-web-shared';
import { MouseEvent, useCallback } from 'react';

import { SxProps, Theme } from '@mui/material/styles';
import { colors } from 'styles/theme/colors';

export interface HeaderItemProps extends MUI.BoxProps {
  label?: string;
  className?: string;
  labelClassName?: string;
  labelSx?: SxProps<Theme>;
  disabled?: boolean;
  readOnly?: boolean;
  showDropDownIcon?: boolean;
  showBorder?: boolean;
  tooltipText?: string;
  testId?: string;
  isPriceFeed?: boolean;
  borderBottomSpace?: number;
}

export const HeaderItem = ({
  className,
  label,
  labelSx,
  disabled = false,
  readOnly = false,
  isPriceFeed = false,
  showDropDownIcon = false,
  showBorder = false,
  tooltipText = '',
  testId,
  onClick,
  ...props
}: HeaderItemProps) => {
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;
  const tooltipOpenProp = tooltipText ? undefined : false;
  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => (disabled ? undefined : onClick && onClick(event)),
    [disabled, onClick],
  );

  const labelColor = isDarkMode ? (isPriceFeed ? colors.baseGrey20 : colors.baseGrey05) : colors.grey40;
  const labelHoverColor = isDarkMode ? (isPriceFeed ? colors.baseGrey20 : colors.baseGrey05) : colors.grey90;

  return (
    <MUI.Box
      className={className}
      onClick={handleClick}
      sx={{
        display: 'flex',
        height: '28px',
        alignItems: 'center',
        padding: '8px',
        cursor: disabled ? 'default' : readOnly ? 'default' : 'pointer',
        position: 'relative',
        color: labelColor,
        '& .label': {
          color: isPriceFeed ? colors.baseGrey20 : colors.baseGrey10,
        },
        '&:hover': {
          borderRadius: '3px',
          background: isDarkMode ? colors.grey51 : colors.baseGrey10,
        },
        ...(disabled && {
          '& .label': { color: theme.palette.text.secondary },
          '& .label:hover': { color: theme.palette.text.secondary },
        }),
      }}
      {...props}
    >
      <MUI.Tooltip title={tooltipText} arrow open={tooltipOpenProp}>
        <MUI.Typography
          variant="h3"
          data-testid={testId}
          className="label"
          sx={{
            fontFamily: 'Akkurat, sans serif',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '21px',
            '&:hover': { color: labelHoverColor },
            ...labelSx,
          }}
        >
          {label}
          {showDropDownIcon && (
            <MUIIcons.ExpandMore
              sx={{ fontSize: '24px !important', fontWeight: 200, marginRight: '-6px', verticalAlign: 'middle' }}
              color="inherit"
            />
          )}
        </MUI.Typography>
      </MUI.Tooltip>
      {showBorder && (
        <MUI.Box
          sx={{
            background: colors.primary,
            width: '100%',
            height: '2px',
            position: 'absolute',
            bottom: '-1px',
            left: 0,
            animation: 'headerItemHighlight 0.25s',
            '@keyframes headerItemHighlight': {
              '0%': { transform: 'scaleX(0)' },
              '100%': { transform: 'scaleX(1)' },
            },
          }}
        />
      )}
    </MUI.Box>
  );
};
